import React, { useContext } from 'react';
import { Link } from 'react-router-dom';
import { EditOutlined, ReloadOutlined } from '@ant-design/icons';
import {
  Button,
  Card,
  Descriptions,
  Empty,
  Result,
  Space,
  Spin,
  Steps,
  StepsProps,
  Table,
  theme,
  Tooltip,
} from 'antd';

import MaintenanceWindowDetailV2Context from '@totem/components/maintenanceWindowsV2/detail/MaintenanceWindowDetailV2Context';
import {
  BypassTag,
  DecisionTag,
  StatusTag,
} from '@totem/components/maintenanceWindowsV2/StatusTags';
import {
  Approval,
  DeviceReference,
  EventReference,
  OverlayNetworkBypass,
} from '@totem/components/maintenanceWindowsV2/types';
import {
  approvalResponseMs,
  endTime,
  formatDateTime,
  formatDuration,
  formatSchedule,
  getApprovalDecision,
  getBuildingLabel,
  getBypassState,
  getTicketIds,
  getWindowStatus,
  isClosed,
  isSetDate,
  startTime,
  toTime,
} from '@totem/components/maintenanceWindowsV2/utilities';
import EventSeverityIndicator from '@totem/components/services/threats/EventSeverityIndicator';
import IBCard from '@totem/components/ui_components/antDesignOverrides/IBCard';
import IBTabs from '@totem/components/ui_components/antDesignOverrides/IBTabs';
import KpiCard from '@totem/components/ui_components/widgets/KpiCard';
import { permissions } from '@totem/components/userContext/security/permissions/permissions';
import { securityGroupCheckConstraintAny } from '@totem/components/userContext/security/userUtilities';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import {
  sortNumberAscending,
  sortStringAscending,
} from '@totem/utilities/tableUtilities';

const cardStyles = { body: { padding: 12 } };

const MaintenanceWindowDetailV2Layout = () => {
  const { loading, notFound, data, now, sending, ticketNumbers, onAction } =
    useContext(MaintenanceWindowDetailV2Context);
  const { userProfile } = useContext(UserProfileContext);
  const { token } = theme.useToken();

  if (loading && data === null) {
    return (
      <IBCard isCovered>
        <div style={{ padding: 40, textAlign: 'center' }}>
          <Spin size="large" />
        </div>
      </IBCard>
    );
  }

  if (notFound || data === null) {
    return (
      <IBCard isCovered>
        <Result
          status="404"
          title="Maintenance window not found"
          subTitle="It may belong to another organization or be outside the buildings you have access to."
          extra={
            <Link to="/dashboard/maintenanceWindows2">
              <Button type="primary">Back to Maintenance Windows</Button>
            </Link>
          }
        />
      </IBCard>
    );
  }

  const { details } = data;
  const status = getWindowStatus(data, now);
  const bypass = getBypassState(data, now);
  const devices = data.devices ?? [];
  const events = data.blockedEvents ?? [];
  const overlays = data.overlayNetworkBypasses ?? [];
  const approvals = data.approvals ?? [];
  const ticketIds = getTicketIds(data);
  const started = startTime(data) <= now;
  const secondary = { color: token.colorTextSecondary, fontSize: 12 };
  const myApproval = approvals.find(
    (approval) =>
      approval.approver.toLowerCase() === userProfile.email.toLowerCase(),
  );
  // Decisions can be changed until the window starts.
  const canDecide =
    myApproval !== undefined &&
    !started &&
    !isClosed(data) &&
    details.requiresBypass;
  const canEdit =
    !isClosed(data) &&
    permissions.maintenanceWindowEditor.hasPermissions(
      userProfile,
      securityGroupCheckConstraintAny,
    );

  const windowLength = endTime(data) - startTime(data);
  const timeKpi = (() => {
    switch (status) {
      case 'active':
        return {
          label: 'Time remaining',
          value: formatDuration(endTime(data) - now),
          sub: `${formatDuration(now - startTime(data))} of ${formatDuration(windowLength)} elapsed`,
          color: token.colorPrimary,
        };
      case 'overdue':
        return {
          label: 'Past scheduled end',
          value: formatDuration(now - endTime(data)),
          sub: 'Not closed by the scheduler; alerts are still suppressed',
          color: token.colorError,
        };
      case 'closed':
        return {
          label: 'Duration',
          value: formatDuration(windowLength),
          sub: isSetDate(details.actualEndTime)
            ? `Closed ${formatDateTime(details.actualEndTime)}`
            : 'Closed',
          color: undefined,
        };
      default:
        return {
          label: 'Starts in',
          value: formatDuration(startTime(data) - now),
          sub: `${formatDuration(windowLength)} window`,
          color: undefined,
        };
    }
  })();

  const approvedBy = approvals.find(
    (approval) => approval.status === 'Approved',
  );
  const criticalEvents = events.filter((event) => event.severity >= 5).length;
  const majorEvents = events.filter((event) => event.severity === 4).length;
  const controlSystems = new Set(
    devices.map((dev) => dev.controlSystem?.id).filter(Boolean),
  ).size;
  const buildings = new Set(
    devices.map((dev) => dev.building?.id).filter(Boolean),
  ).size;

  const steps: StepsProps['items'] = [
    {
      title: 'Created',
      status: 'finish',
      content: `${formatDateTime(data.createdAt)} · ${data.createdBy}`,
    },
  ];
  if (details.requiresBypass) {
    steps.push({
      title: bypass === 'rejected' ? 'Bypass rejected' : 'Bypass approved',
      status: details.bypassApproved
        ? 'finish'
        : bypass === 'rejected'
          ? 'error'
          : 'process',
      content: approvedBy
        ? `${formatDateTime(approvedBy.approvedAt)} · ${approvedBy.approver}`
        : bypass === 'rejected'
          ? 'Window will run without remote access'
          : `Waiting on ${approvals.length} approver${approvals.length === 1 ? '' : 's'}`,
    });
  }
  steps.push({
    title: 'Window opened',
    status: started ? 'finish' : 'wait',
    content: `${started ? '' : 'Scheduled '}${formatDateTime(details.scheduledStartTime)} · alerts suppressed`,
  });
  if (details.requiresBypass && details.bypassApproved) {
    steps.push({
      title: 'Overlay created',
      status: overlays.length > 0 ? 'finish' : started ? 'error' : 'wait',
      content:
        overlays.length > 0
          ? overlays.map((overlay) => overlay.overlayNetworkName).join(', ')
          : started
            ? 'No overlay network was recorded'
            : 'Created when the window opens',
    });
  }
  steps.push({
    title:
      details.requiresBypass && details.bypassApproved
        ? 'Closed & overlay removed'
        : 'Window closed',
    status:
      status === 'closed'
        ? 'finish'
        : status === 'overdue'
          ? 'error'
          : status === 'active'
            ? 'process'
            : 'wait',
    content:
      status === 'closed'
        ? formatDateTime(
            isSetDate(details.actualEndTime)
              ? details.actualEndTime
              : details.scheduledEndTime,
          )
        : status === 'overdue'
          ? `Was due ${formatDateTime(details.scheduledEndTime)}`
          : `Scheduled ${formatDateTime(details.scheduledEndTime)}`,
  });

  const approvalColumns = [
    {
      title: 'Approver',
      key: 'approver',
      render: (_, approval: Approval) =>
        approval === myApproval ? (
          <b>{approval.approver} (you)</b>
        ) : (
          approval.approver
        ),
    },
    {
      title: 'Requested',
      key: 'requested',
      render: (_, approval: Approval) => formatDateTime(approval.requestedAt),
    },
    {
      title: 'Decision',
      key: 'decision',
      render: (_, approval: Approval) => (
        <DecisionTag decision={getApprovalDecision(data, approval)} />
      ),
    },
    {
      title: 'Decided',
      key: 'decided',
      render: (_, approval: Approval) =>
        approval.status === 'Approved' || approval.status === 'Rejected'
          ? formatDateTime(approval.approvedAt)
          : '-',
    },
    {
      title: 'Took',
      key: 'took',
      align: 'right' as const,
      render: (_, approval: Approval) =>
        getApprovalDecision(data, approval) === 'pending' ? (
          <span style={secondary}>
            waiting {formatDuration(now - toTime(approval.requestedAt))}
          </span>
        ) : (
          formatDuration(approvalResponseMs(approval))
        ),
    },
  ];

  const deviceColumns = [
    {
      title: 'Device',
      key: 'device',
      render: (_, dev: DeviceReference) => (
        <Link to={`/dashboard/devices/${dev.device.id}`}>
          {dev.device.name}
        </Link>
      ),
      sorter: (compA: DeviceReference, compB: DeviceReference) =>
        sortStringAscending(compA.device.name, compB.device.name),
    },
    {
      title: 'Region',
      key: 'region',
      render: (_, dev: DeviceReference) => dev.region?.name,
    },
    {
      title: 'Building',
      key: 'building',
      render: (_, dev: DeviceReference) => dev.building?.name,
    },
    {
      title: 'Control system',
      key: 'controlSystem',
      render: (_, dev: DeviceReference) => dev.controlSystem?.name,
    },
    {
      title: 'Alerts suppressed',
      key: 'alerts',
      align: 'right' as const,
      render: (_, dev: DeviceReference) =>
        events.filter((event) => event.device?.id === dev.device.id).length,
    },
  ];

  const eventColumns = [
    {
      title: 'Severity',
      key: 'severity',
      width: 95,
      render: (_, event: EventReference) => (
        <EventSeverityIndicator severity={event.severity} />
      ),
      sorter: (compA: EventReference, compB: EventReference) =>
        sortNumberAscending(compA.severity, compB.severity),
    },
    {
      title: 'Time',
      key: 'time',
      render: (_, event: EventReference) => formatDateTime(event.eventTime),
      defaultSortOrder: 'descend' as const,
      sorter: (compA: EventReference, compB: EventReference) =>
        sortNumberAscending(toTime(compA.eventTime), toTime(compB.eventTime)),
    },
    {
      title: 'Device',
      key: 'device',
      render: (_, event: EventReference) =>
        event.device?.id ? (
          <Link to={`/dashboard/devices/${event.device.id}`}>
            {event.device.name}
          </Link>
        ) : (
          '-'
        ),
    },
    {
      title: 'Source',
      key: 'source',
      render: (_, event: EventReference) =>
        `${event.sourceSystem} · ${event.sourceType}`,
    },
    { title: 'Type', dataIndex: 'eventType', key: 'eventType' },
    { title: 'Description', dataIndex: 'description', key: 'description' },
  ];

  return (
    <IBCard
      isCovered
      title={
        <Space wrap size={8}>
          <span>{details.reason || 'Maintenance window'}</span>
          <StatusTag status={status} />
          <BypassTag state={bypass} />
        </Space>
      }
      extra={
        <Space wrap>
          {canDecide && myApproval.status !== 'Rejected' && (
            <Button
              danger
              disabled={sending}
              onClick={() => onAction('reject')}
            >
              Reject bypass
            </Button>
          )}
          {canDecide && myApproval.status !== 'Approved' && (
            <Button
              type="primary"
              disabled={sending}
              onClick={() => onAction('approve')}
            >
              Approve bypass
            </Button>
          )}
          {canEdit && (
            <Button icon={<EditOutlined />} onClick={() => onAction('edit')}>
              Edit
            </Button>
          )}
          <Tooltip title="Refresh">
            <Button
              icon={<ReloadOutlined />}
              loading={loading}
              aria-label="Refresh"
              onClick={() => onAction('refresh')}
            />
          </Tooltip>
        </Space>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div style={{ ...secondary, fontSize: 13 }}>
          <Link to="/dashboard/maintenanceWindows2">Maintenance Windows</Link> ·{' '}
          {getBuildingLabel(data)} · {formatSchedule(data)}
          {ticketIds.map((ticketId) => (
            <React.Fragment key={ticketId}>
              {' '}
              ·{' '}
              <Link to={`/dashboard/ticket/${ticketId}`}>
                {ticketNumbers[ticketId]
                  ? `Ticket ${ticketNumbers[ticketId]}`
                  : 'Ticket'}
              </Link>
            </React.Fragment>
          ))}
        </div>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))',
            gap: 10,
          }}
        >
          <KpiCard
            label={timeKpi.label}
            value={timeKpi.value}
            sub={timeKpi.sub}
            valueColor={timeKpi.color}
          />
          <KpiCard
            label="Devices in scope"
            value={devices.length}
            sub={`${controlSystems} control system${controlSystems === 1 ? '' : 's'} · ${buildings} building${buildings === 1 ? '' : 's'}`}
          />
          <KpiCard
            label="Alerts suppressed"
            value={events.length}
            sub={`${criticalEvents} critical · ${majorEvents} major`}
            valueColor={criticalEvents > 0 ? token.colorError : undefined}
          />
          <KpiCard
            label="Firewall bypass"
            value={
              !details.requiresBypass
                ? 'Not required'
                : approvedBy
                  ? 'Approved'
                  : bypass === 'rejected'
                    ? 'Rejected'
                    : 'Pending'
            }
            sub={
              approvedBy
                ? `by ${approvedBy.approver} in ${formatDuration(approvalResponseMs(approvedBy))}`
                : details.requiresBypass
                  ? `${approvals.length} approver${approvals.length === 1 ? '' : 's'} asked`
                  : 'No remote access requested'
            }
            valueColor={
              bypass === 'rejected'
                ? token.colorError
                : approvedBy
                  ? token.colorSuccess
                  : details.requiresBypass
                    ? token.colorWarning
                    : undefined
            }
          />
        </div>

        <Card size="small" title="Lifecycle" styles={cardStyles}>
          <Steps size="small" items={steps} responsive />
        </Card>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns:
              'repeat(auto-fit, minmax(min(100%, 420px), 1fr))',
            gap: 16,
          }}
        >
          <Card
            size="small"
            title="Overlay networks"
            extra={<span style={{ fontSize: 12 }}>Airwall bypass</span>}
            styles={cardStyles}
          >
            {!details.requiresBypass ? (
              <Empty description="This window does not request a firewall bypass" />
            ) : overlays.length === 0 ? (
              <Empty
                description={
                  bypass === 'notCreated'
                    ? 'The bypass was approved but no overlay network was recorded'
                    : 'An overlay network is created when the window opens'
                }
              />
            ) : (
              <div
                style={{ display: 'flex', flexDirection: 'column', gap: 10 }}
              >
                {overlays.map((overlay: OverlayNetworkBypass) => (
                  <div
                    key={overlay.overlayNetworkId || overlay.overlayNetworkName}
                    style={{
                      border: `1px solid ${token.colorBorderSecondary}`,
                      borderRadius: 8,
                      padding: 12,
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        gap: 10,
                        flexWrap: 'wrap',
                      }}
                    >
                      <div>
                        <div
                          style={{ fontWeight: 600, wordBreak: 'break-all' }}
                        >
                          {overlay.overlayNetworkName}
                        </div>
                        <div style={secondary}>{overlay.overlayNetworkId}</div>
                      </div>
                      <BypassTag state={bypass} />
                    </div>
                    <div style={{ marginTop: 8, fontSize: 13 }}>
                      {(overlay.devices ?? []).length > 0 ? (
                        (overlay.devices ?? [])
                          .map((member) => member.name)
                          .join(', ')
                      ) : (
                        <span style={secondary}>
                          No overlay members recorded
                        </span>
                      )}
                    </div>
                  </div>
                ))}
                <div style={secondary}>
                  The overlay is removed automatically when the window closes.
                </div>
              </div>
            )}
          </Card>

          <Card
            size="small"
            title="Approvals"
            extra={
              <span style={{ fontSize: 12 }}>
                Any one approver approves the bypass
              </span>
            }
            styles={cardStyles}
          >
            {approvals.length === 0 ? (
              <Empty description="No approvals were requested" />
            ) : (
              <Table
                size="small"
                pagination={false}
                scroll={{ x: true }}
                dataSource={approvals}
                rowKey={(approval) => approval.approver}
                columns={approvalColumns}
              />
            )}
            <Descriptions
              size="small"
              column={1}
              style={{ marginTop: 12 }}
              items={[
                {
                  key: 'reason',
                  label: 'Reason',
                  children: details.reason || '-',
                },
                {
                  key: 'description',
                  label: 'Description',
                  children: details.description || '-',
                },
                {
                  key: 'contact',
                  label: 'Site contact',
                  children:
                    [
                      details.siteContactName,
                      details.siteContactPhone,
                      details.siteContactEmail,
                    ]
                      .filter(Boolean)
                      .join(' · ') || '-',
                },
                {
                  key: 'updated',
                  label: 'Last updated',
                  children: `${formatDateTime(data.updatedAt)} · ${data.updatedBy}`,
                },
              ]}
            />
          </Card>
        </div>

        <IBTabs
          items={[
            {
              key: 'devices',
              label: `Devices (${devices.length})`,
              children: (
                <Table
                  size="small"
                  scroll={{ x: true }}
                  dataSource={devices}
                  rowKey={(dev) => dev.device.id}
                  columns={deviceColumns}
                  pagination={{ defaultPageSize: 20, showSizeChanger: true }}
                />
              ),
            },
            {
              key: 'events',
              label: `Suppressed alerts (${events.length})`,
              children: (
                <Table
                  size="small"
                  scroll={{ x: true }}
                  dataSource={events}
                  rowKey={(event) => event.id}
                  columns={eventColumns}
                  locale={{ emptyText: 'No alerts were suppressed' }}
                  pagination={{ defaultPageSize: 20, showSizeChanger: true }}
                />
              ),
            },
          ]}
        />
      </div>
    </IBCard>
  );
};

export default MaintenanceWindowDetailV2Layout;
